import React, { useMemo, useState } from 'react';
import { Popover, Checkbox, Input, Button } from 'antd';
import { SearchOutlined, DownOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

export interface FormCheckboxDropdownOption {
  id: number;
  name: string;
}

export interface FormCheckboxDropdownProps {
  value?: number[];
  onChange?: (vals: number[]) => void;
  options: FormCheckboxDropdownOption[];
  placeholder?: string;
  icon?: React.ReactNode;
  searchPlaceholder?: string;
  clearLabel?: string;
  emptyLabel?: string;
  disabled?: boolean;
}

const FormCheckboxDropdown: React.FC<FormCheckboxDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder,
  icon,
  searchPlaceholder,
  clearLabel,
  emptyLabel,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const selectedValues = useMemo(() => value || [], [value]);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase().trim();
    return options.filter((o) => (o.name || '').toLowerCase().includes(query));
  }, [options, searchQuery]);

  const toggleValue = (id: number) => {
    if (disabled) return;
    let next: number[];
    if (selectedValues.includes(id)) {
      next = selectedValues.filter((x) => x !== id);
    } else {
      next = [...selectedValues, id];
    }
    onChange?.(next);
  };

  return (
    <Popover
      trigger="click"
      placement="bottomLeft"
      open={open && !disabled}
      onOpenChange={(v) => {
        if (!disabled) {
          setOpen(v);
          if (!v) setSearchQuery('');
        }
      }}
      content={
        <div className="crm-pill-dropdown-content" style={{ width: 280 }}>
          {options.length > 4 && (
            <div className="crm-pill-dropdown-search">
              <Input
                size="small"
                placeholder={searchPlaceholder || t('Search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                allowClear
                prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              />
            </div>
          )}
          <div className="crm-pill-dropdown-list" style={{ maxHeight: 220, overflowY: 'auto' }}>
            {filteredOptions.length === 0 ? (
              <div className="crm-pill-dropdown-empty">{emptyLabel || t('NoData')}</div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = selectedValues.includes(opt.id);
                return (
                  <div
                    key={opt.id}
                    className={`crm-pill-dropdown-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => toggleValue(opt.id)}
                  >
                    <Checkbox checked={isSelected} />
                    <span className="crm-pill-dropdown-item-text">{opt.name}</span>
                  </div>
                );
              })
            )}
          </div>
          {selectedValues.length > 0 && (
            <div
              className="crm-pill-dropdown-footer"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '6px 8px 2px',
              }}
            >
              <span style={{ fontSize: 12, color: '#64748b' }}>
                {t('Selected', 'Обрано')}: <b>{selectedValues.length}</b>
              </span>
              <Button
                type="link"
                size="small"
                onClick={() => onChange?.([])}
                style={{ padding: 0, fontSize: 12 }}
              >
                {clearLabel || t('Clean', 'Очистити')}
              </Button>
            </div>
          )}
        </div>
      }
    >
      <div
        className={`crm-form-checkbox-dropdown-trigger ${
          selectedValues.length > 0 ? 'has-value' : ''
        } ${disabled ? 'disabled' : ''}`}
      >
        <div className="crm-trigger-content">
          {icon && <span className="crm-trigger-icon">{icon}</span>}
          {selectedValues.length === 0 ? (
            <span className="crm-trigger-placeholder">{placeholder || t('Select')}</span>
          ) : (
            <div className="crm-trigger-tags">
              {selectedValues.slice(0, 2).map((id) => {
                const opt = options.find((o) => o.id === id);
                return opt ? (
                  <span key={id} className="crm-trigger-tag" title={opt.name}>
                    {opt.name}
                  </span>
                ) : null;
              })}
              {selectedValues.length > 2 && (
                <span className="crm-trigger-tag-more">
                  +{selectedValues.length - 2}
                </span>
              )}
            </div>
          )}
        </div>
        <DownOutlined className="crm-trigger-arrow" />
      </div>
    </Popover>
  );
};

export default FormCheckboxDropdown;
